/**
 * Google Analytics 4 Service Layer
 * Provides centralized tracking for the waitlist signup flow and survey journey
 */

import { sha256 } from '../utils/analyticsHelpers';

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.debugMode = process.env.NODE_ENV === 'development';
    this.measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    this.sessionData = {
      startTime: Date.now(),
      surveyStartTime: null,
      lastStepTime: null,
      utmParams: this.captureUTMParams(),
    };

    this.initialize();
  }

  /**
   * Initialize GA4 if not already initialized
   */
  initialize() {
    if (!window.gtag || !this.measurementId || this.isInitialized) {
      if (this.debugMode && !this.measurementId) {
        console.log('[GA4] No measurement ID provided, analytics disabled');
      }
      return;
    }

    this.isInitialized = true;

    if (this.debugMode) {
      console.log('[GA4] Analytics initialized with ID:', this.measurementId);
    }

    // Set default consent state
    this.updateConsent();
  }

  /**
   * Update consent state based on user preferences
   */
  updateConsent(granted = true) {
    if (!window.gtag) return;

    window.gtag('consent', 'update', {
      'analytics_storage': granted ? 'granted' : 'denied',
      'functionality_storage': 'granted',
      'personalization_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });
  }

  /**
   * Capture UTM parameters from URL
   */
  captureUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });

    return utmParams;
  }

  /**
   * Core event tracking method
   * @param {string} eventName - Name of the event
   * @param {Object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    if (!window.gtag || !this.isInitialized) {
      if (this.debugMode) {
        console.log('[GA4] Event not sent (not initialized):', eventName, parameters);
      }
      return;
    }

    // Add UTM params to all events
    const enrichedParams = {
      ...this.sessionData.utmParams,
      ...this.sanitizeParameters(parameters),
      timestamp: new Date().toISOString(),
    };

    if (this.debugMode) {
      console.log('[GA4] Event:', eventName, enrichedParams);
    }

    window.gtag('event', eventName, enrichedParams);
  }

  /**
   * Track a conversion event
   * @param {string} eventName - Name of the conversion event
   * @param {Object} parameters - Event parameters
   * @param {number} value - Conversion value (optional)
   */
  trackConversion(eventName, parameters = {}, value = null) {
    const conversionParams = {
      ...parameters,
      send_to: this.measurementId,
    };

    if (value !== null) {
      conversionParams.value = value;
      conversionParams.currency = 'USD';
    }

    this.trackEvent(eventName, conversionParams);
  }

  /**
   * Set user property
   * @param {string} property - Property name
   * @param {any} value - Property value
   */
  setUserProperty(property, value) {
    if (!window.gtag || !this.isInitialized) return;

    window.gtag('set', 'user_properties', {
      [property]: value
    });

    if (this.debugMode) {
      console.log('[GA4] User property set:', property, value);
    }
  }

  /**
   * Track timing events
   * @param {string} category - Timing category
   * @param {string} variable - Timing variable
   * @param {number} time - Time in milliseconds
   */
  trackTiming(category, variable, time) {
    this.trackEvent('timing_complete', {
      name: variable,
      value: Math.round(time),
      event_category: category
    });
  }

  /**
   * Sanitize parameters to ensure no PII
   * @param {Object} params - Raw parameters
   * @returns {Object} Sanitized parameters
   */
  sanitizeParameters(params) {
    const sanitized = {};

    Object.keys(params).forEach(key => {
      let value = params[key];

      // Skip null/undefined values
      if (value === null || value === undefined) {
        return;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        sanitized[key] = value.map(v => String(v).slice(0, 100));
        return;
      }

      // Truncate strings
      if (typeof value === 'string') {
        sanitized[key] = value.slice(0, 100);
        return;
      }

      // Pass through numbers and booleans
      sanitized[key] = value;
    });

    return sanitized;
  }

  // ============================================
  // WAITLIST TRACKING EVENTS
  // ============================================

  /**
   * Track when user starts filling out waitlist form
   * @param {string} formPosition - 'header' or 'footer'
   */
  trackWaitlistStart(formPosition) {
    this.trackEvent('waitlist_signup_start', {
      form_position: formPosition,
      device_type: this.getDeviceType(),
      page_location: window.location.pathname,
      referrer: document.referrer
    });
  }

  /**
   * Track when user submits waitlist form
   * @param {string} formPosition - 'header' or 'footer'
   * @param {string} email - User's email (will be hashed)
   */
  async trackWaitlistSubmit(formPosition, email) {
    const emailDomain = email.split('@')[1];
    const hashedDomain = await sha256(emailDomain);

    this.trackEvent('waitlist_signup_submit', {
      form_position: formPosition,
      email_domain_hashed: hashedDomain.slice(0, 16), // First 16 chars of hash
      referrer: document.referrer
    });
  }

  /**
   * Track successful waitlist signup (CONVERSION EVENT)
   * @param {string} formPosition - 'header' or 'footer'
   * @param {Object} result - API response data
   */
  trackWaitlistSuccess(formPosition, result) {
    this.trackConversion('waitlist_signup_success', {
      form_position: formPosition,
      already_subscribed: result.already_subscribed || false,
      welcome_email_sent: result.welcome_email_sent || false
    });
  }

  /**
   * Track waitlist signup error
   * @param {string} formPosition - 'header' or 'footer'
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   */
  trackWaitlistError(formPosition, errorType, errorMessage) {
    this.trackEvent('waitlist_signup_error', {
      form_position: formPosition,
      error_type: errorType,
      error_message: errorMessage.slice(0, 100) // Truncate for privacy
    });
  }

  // ============================================
  // SURVEY TRACKING EVENTS
  // ============================================

  /**
   * Track when survey modal opens
   * @param {string} triggerSource - How survey was triggered
   * @param {string} email - User's email (will be hashed)
   */
  async trackSurveyStart(triggerSource, email) {
    this.sessionData.surveyStartTime = Date.now();
    this.sessionData.lastStepTime = Date.now();

    const emailDomain = email ? email.split('@')[1] : 'unknown';
    const hashedDomain = await sha256(emailDomain);

    this.trackEvent('survey_start', {
      trigger_source: triggerSource,
      email_domain_hashed: hashedDomain.slice(0, 16)
    });
  }

  /**
   * Track when a survey step is viewed
   * @param {number} stepNumber - Step number (1-4)
   * @param {string} stepName - Name of the step
   */
  trackSurveyStepView(stepNumber, stepName) {
    const now = Date.now();
    const timeOnPreviousStep = this.sessionData.lastStepTime
      ? Math.round((now - this.sessionData.lastStepTime) / 1000)
      : 0;

    this.trackEvent('survey_step_view', {
      step_number: stepNumber,
      step_name: stepName,
      time_on_previous_step: timeOnPreviousStep
    });

    this.sessionData.lastStepTime = now;
  }

  /**
   * Track when a survey step is completed
   * @param {number} stepNumber - Step number
   * @param {string} stepName - Name of the step
   * @param {string} answerCategory - Generalized answer category
   */
  trackSurveyStepComplete(stepNumber, stepName, answerCategory) {
    const timeOnStep = this.sessionData.lastStepTime
      ? Math.round((Date.now() - this.sessionData.lastStepTime) / 1000)
      : 0;

    this.trackEvent('survey_step_complete', {
      step_number: stepNumber,
      step_name: stepName,
      time_on_step: timeOnStep,
      answer_category: answerCategory
    });
  }

  /**
   * Track when a survey step is skipped
   * @param {number} stepNumber - Step number
   * @param {string} stepName - Name of the step
   */
  trackSurveyStepSkip(stepNumber, stepName) {
    const timeOnStep = this.sessionData.lastStepTime
      ? Math.round((Date.now() - this.sessionData.lastStepTime) / 1000)
      : 0;

    this.trackEvent('survey_step_skip', {
      step_number: stepNumber,
      step_name: stepName,
      time_on_step: timeOnStep
    });
  }

  /**
   * Track survey completion (CONVERSION EVENT)
   * @param {number} stepsCompleted - Number of steps completed
   * @param {number} stepsSkipped - Number of steps skipped
   */
  trackSurveyComplete(stepsCompleted, stepsSkipped) {
    const totalTime = this.sessionData.surveyStartTime
      ? Math.round((Date.now() - this.sessionData.surveyStartTime) / 1000)
      : 0;

    const completionRate = Math.round((stepsCompleted / 4) * 100);

    this.trackConversion('survey_complete', {
      completion_rate: completionRate,
      total_time: totalTime,
      steps_completed: stepsCompleted,
      steps_skipped: stepsSkipped
    });
  }

  /**
   * Track when survey is abandoned
   * @param {number} lastStep - Last step viewed
   * @param {number} stepsCompleted - Number of steps completed
   */
  trackSurveyAbandon(lastStep, stepsCompleted) {
    const timeSpent = this.sessionData.surveyStartTime
      ? Math.round((Date.now() - this.sessionData.surveyStartTime) / 1000)
      : 0;

    const completionRate = Math.round((stepsCompleted / 4) * 100);

    this.trackEvent('survey_abandon', {
      last_step: lastStep,
      time_spent: timeSpent,
      completion_rate: completionRate
    });
  }

  // ============================================
  // INTERACTION TRACKING EVENTS
  // ============================================

  /**
   * Track location search interaction
   * @param {string} searchQuery - Search query
   * @param {number} resultsCount - Number of results
   * @param {boolean} selectionMade - Whether user selected a result
   */
  trackLocationSearch(searchQuery, resultsCount, selectionMade) {
    this.trackEvent('location_search', {
      search_query: searchQuery.slice(0, 3), // Only first 3 chars for privacy
      results_count: resultsCount,
      selection_made: selectionMade
    });
  }

  /**
   * Track event type selection
   * @param {Array} eventTypes - Selected event types
   * @param {number} stepNumber - Step number
   */
  trackEventTypeSelect(eventTypes, stepNumber) {
    this.trackEvent('event_type_select', {
      event_types: eventTypes,
      count: eventTypes.length,
      step_number: stepNumber
    });
  }

  /**
   * Track frequency selection
   * @param {string} frequencyValue - Selected frequency
   * @param {number} stepNumber - Step number
   */
  trackFrequencySelect(frequencyValue, stepNumber) {
    this.trackEvent('frequency_select', {
      frequency_value: frequencyValue,
      step_number: stepNumber
    });
  }

  /**
   * Track pain point selection
   * @param {Array} painPoints - Selected pain points
   * @param {number} stepNumber - Step number
   */
  trackPainPointSelect(painPoints, stepNumber) {
    this.trackEvent('pain_point_select', {
      pain_points: painPoints,
      count: painPoints.length,
      step_number: stepNumber
    });
  }

  /**
   * Track device type selection
   * @param {string} deviceType - Selected device type
   * @param {number} stepNumber - Step number
   */
  trackDeviceSelect(deviceType, stepNumber) {
    this.trackEvent('device_select', {
      device_type: deviceType,
      step_number: stepNumber
    });
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Get device type based on screen size
   * @returns {string} Device type
   */
  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Set multiple user properties at once
   * @param {Object} properties - Properties to set
   */
  setUserProperties(properties) {
    Object.keys(properties).forEach(key => {
      this.setUserProperty(key, properties[key]);
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

// Export for use in components
export default analytics;